import { graphqlRequest } from '@dehaat/kisan-app-bl/graphql'
import { Brand } from '@dehaat/kisan-app-bl/models/Product'
import { gql } from 'graphql-request'

import ApiResponse from '@/models/ApiResponse'
import { Product2 } from '@/models/Product'
import { get, getOptional } from '@/utils/axios'

// The products API returns at most ~1000 rows per page. Larger pages are
// much faster overall than many small ones.
const PAGE_SIZE = 1000
// Pages requested before the total count is known; pages past the end 404
const SPECULATIVE_PAGES = 4
const MAX_PAGES = 10
// The GraphQL API allows at most 10 aliases per request
const LOOKUP_CHUNK_SIZE = 10
const MAX_PARALLEL_LOOKUPS = 10
// A few brand names belong to more than one brand
const MAX_NAME_MATCHES = 5

type BrandLookupResponse = Record<string, { edges: { node: Brand }[] }>

// English, because the GraphQL name lookup only matches English names
const getProductsPageParams = (vendorId: string, page: number) => ({
  vendor_id: vendorId,
  page,
  page_size: PAGE_SIZE,
  lang: 'en',
  isprebook: true,
})

const fetchProductsPage = (vendorId: string, page: number) =>
  page === 1
    ? get<ApiResponse<Product2>>(
        '/hyperlocal/v1/products',
        getProductsPageParams(vendorId, page),
      )
    : getOptional<ApiResponse<Product2>>(
        '/hyperlocal/v1/products',
        getProductsPageParams(vendorId, page),
      )

const getBrandNames = (products: Product2[]) => {
  const names = new Set<string>()
  products.forEach(({ brand }) => {
    const name = typeof brand === 'string' ? brand : brand?.name
    if (name) names.add(name)
  })
  return names
}

const lookupBrandsChunk = async (names: string[], lang: string) => {
  const fields = names.map(
    (name, index) => `
      b${index}: allProductBrands(
        nearby: false,
        lang: ${JSON.stringify(lang)},
        name: ${JSON.stringify(name)},
        first: ${MAX_NAME_MATCHES},
        offset: 0
      ) {
        edges {
          node {
            id
            name
            imageUrl
            slug {
              slug
              redirectUrl
              metadata
            }
          }
        }
      }`,
  )
  const response = await graphqlRequest<BrandLookupResponse>(
    gql`{ ${fields.join('\n')} }`,
  )
  return Object.values(response).map(({ edges }) =>
    edges.map(({ node }) => node),
  )
}

const hasPrebookProducts = async (vendorId: string, brandId: string) => {
  const response = await getOptional<ApiResponse<Product2>>(
    '/hyperlocal/v1/products',
    {
      vendor_id: vendorId,
      brand_id: brandId,
      page_size: 1,
      lang: 'en',
      isprebook: true,
    },
  )
  return Boolean(response?.count)
}

// Keeps the brands that have products at this vendor. If none can be
// confirmed, keeps the first match.
const pickBrandsWithProducts = async (vendorId: string, matches: Brand[]) => {
  if (matches.length <= 1) return matches
  const results = await Promise.allSettled(
    matches.map(({ id }) => hasPrebookProducts(vendorId, id)),
  )
  const withProducts = matches.filter((_, index) => {
    const result = results[index]
    return result.status === 'fulfilled' && result.value
  })
  return withProducts.length > 0 ? withProducts : matches.slice(0, 1)
}

// Products only carry the brand name, so look up id, logo and slug by name.
// A failed lookup drops only that chunk's brands.
const lookupBrands = async (
  vendorId: string,
  names: string[],
  lang: string,
) => {
  const chunks: string[][] = []
  for (let i = 0; i < names.length; i += LOOKUP_CHUNK_SIZE) {
    chunks.push(names.slice(i, i + LOOKUP_CHUNK_SIZE))
  }
  const brands: Brand[] = []
  for (let i = 0; i < chunks.length; i += MAX_PARALLEL_LOOKUPS) {
    const results = await Promise.allSettled(
      chunks
        .slice(i, i + MAX_PARALLEL_LOOKUPS)
        .map((chunk) => lookupBrandsChunk(chunk, lang)),
    )
    const matchesPerName = results.flatMap((result) =>
      result.status === 'fulfilled' ? result.value : [],
    )
    const picked = await Promise.all(
      matchesPerName.map((matches) =>
        pickBrandsWithProducts(vendorId, matches),
      ),
    )
    brands.push(...picked.flat())
  }
  return brands
}

const sortBrands = (brands: Brand[]) =>
  [...brands].sort((a, b) => a.name.localeCompare(b.name))

// Brands of every product in the vendor's prebook listing. The brand APIs
// have no prebook flag, so they miss brands that only appear with it.
// Large vendors span several slow pages, so onPartial gets the brands found
// so far after each page.
const fetchPrebookBrands = async (
  vendorId: string,
  lang: string,
  onPartial?: (brands: Brand[]) => void,
) => {
  const brandsById = new Map<string, Brand>()
  const seenNames = new Set<string>()

  const addPage = async (page: number) => {
    const response = await fetchProductsPage(vendorId, page)
    if (!response) return undefined
    const newNames = Array.from(getBrandNames(response.results)).filter(
      (name) => !seenNames.has(name),
    )
    newNames.forEach((name) => seenNames.add(name))
    const brands = await lookupBrands(vendorId, newNames, lang)
    brands.forEach((brand) => brandsById.set(brand.id, brand))
    if (brands.length > 0) {
      onPartial?.(sortBrands(Array.from(brandsById.values())))
    }
    return response.count
  }

  const pageRange = (from: number, to: number) =>
    Array.from({ length: Math.max(to - from + 1, 0) }, (_, i) => from + i)

  const [firstPage] = await Promise.allSettled(
    pageRange(1, SPECULATIVE_PAGES).map(addPage),
  )
  if (firstPage.status === 'rejected') throw firstPage.reason

  const pageCount = Math.min(
    Math.ceil((firstPage.value ?? 0) / PAGE_SIZE),
    MAX_PAGES,
  )
  await Promise.allSettled(
    pageRange(SPECULATIVE_PAGES + 1, pageCount).map(addPage),
  )

  return sortBrands(Array.from(brandsById.values()))
}

export default fetchPrebookBrands
